import Phaser from 'phaser';

const TEXTURE_SIZE = 128;
const SHADOW_PADDING = 6;
const CANVAS_SIZE = TEXTURE_SIZE + SHADOW_PADDING * 2;
const DICE_DIM_ALPHA = 0.4;

function pipOffsets(face: number): Array<[number, number]> {
  // Offsets in [-1, 0, 1] units, scaled by pipDist when drawing.
  const map: Record<number, Array<[number, number]>> = {
    1: [[0, 0]],
    2: [[-1, -1], [1, 1]],
    3: [[-1, -1], [0, 0], [1, 1]],
    4: [[-1, -1], [1, -1], [-1, 1], [1, 1]],
    5: [[-1, -1], [1, -1], [0, 0], [-1, 1], [1, 1]],
    6: [[-1, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [1, 1]],
  };
  return map[face] ?? [];
}

function drawDieFace(ctx: CanvasRenderingContext2D, face: number) {
  const x0 = SHADOW_PADDING;
  const y0 = SHADOW_PADDING;
  const w = TEXTURE_SIZE;
  const h = TEXTURE_SIZE;
  const cx = x0 + w / 2;
  const cy = y0 + h / 2;

  // Drop shadow.
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.fillRect(x0 + 2, y0 + 4, w, h);

  // Body — radial gradient: bright cherry in the center, deep ruby at the
  // edges. This is what gives translucent dice their "lit from within" feel.
  const bodyGrad = ctx.createRadialGradient(
    cx,
    cy,
    w * 0.05,
    cx,
    cy,
    w * 0.75,
  );
  bodyGrad.addColorStop(0, '#e63a4d');
  bodyGrad.addColorStop(0.55, '#cc1a30');
  bodyGrad.addColorStop(1, '#9a1525');
  ctx.fillStyle = bodyGrad;
  ctx.fillRect(x0, y0, w, h);

  // Sharp dark rim.
  ctx.strokeStyle = '#3a050a';
  ctx.lineWidth = 2;
  ctx.strokeRect(x0 + 1, y0 + 1, w - 2, h - 2);

  // Inner sheen — faint top-edge highlight.
  ctx.strokeStyle = 'rgba(255,255,255,0.18)';
  ctx.lineWidth = 1;
  ctx.strokeRect(x0 + 3, y0 + 3, w - 6, h - 6);

  // Pips with halo.
  const pipDist = w * 0.25;
  const pipR = w * 0.085;
  const oneR = w * 0.13;
  const r = face === 1 ? oneR : pipR;
  for (const [ox, oy] of pipOffsets(face)) {
    const px = cx + ox * pipDist;
    const py = cy + oy * pipDist;

    // Soft halo around the pip — suggests the recessed white catching the
    // surrounding red plastic's internal glow.
    const halo = ctx.createRadialGradient(px, py, r * 0.4, px, py, r * 1.7);
    halo.addColorStop(0, 'rgba(255,255,255,0.45)');
    halo.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(px, py, r * 1.7, 0, Math.PI * 2);
    ctx.fill();

    // Solid white pip.
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(px, py, r, 0, Math.PI * 2);
    ctx.fill();

    // Recessed-edge shadow.
    ctx.strokeStyle = 'rgba(0,0,0,0.18)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(px, py, r, 0, Math.PI * 2);
    ctx.stroke();
  }
}

function getDieTexture(scene: Phaser.Scene, face: number): string {
  const key = `die-${face}`;
  if (scene.textures.exists(key)) return key;
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_SIZE;
  canvas.height = CANVAS_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Die canvas 2D context unavailable');
  drawDieFace(ctx, face);
  scene.textures.addCanvas(key, canvas);
  return key;
}

export class Dice extends Phaser.GameObjects.Container {
  private sprite: Phaser.GameObjects.Sprite;
  private halo: Phaser.GameObjects.Graphics | undefined;
  private haloTween: Phaser.Tweens.Tween | undefined;
  private idleJiggleTween: Phaser.Tweens.Tween | undefined;
  private alphaTween: Phaser.Tweens.Tween | undefined;
  private rollReady = false;
  private readonly idleJigglePhase = Math.random() * Math.PI * 2;

  constructor(scene: Phaser.Scene, x: number, y: number, size = 48) {
    super(scene, x, y);
    this.sprite = scene.add.sprite(0, 0, getDieTexture(scene, 1));
    this.sprite.setScale(size / TEXTURE_SIZE);
    this.add(this.sprite);
    this.setSize(size, size);
    // No setInteractive: pointer input is owned by a shared hit rectangle in
    // the scene (DiceThrow wiring). Making the dice individually interactive
    // would block bet-zone taps whenever the dice happen to fly over a zone
    // mid-throw, since Phaser's hit test is topOnly by default.
    scene.add.existing(this);
    // Start dimmed — the bridge will flip rollReady when the gate opens
    // (e.g., a Pass Line bet lands), at which point we fade in + jiggle.
    this.setAlpha(DICE_DIM_ALPHA);
  }

  /**
   * Reflect whether the dice can currently be thrown. Drives both the idle
   * jiggle and the dimmed-vs-bright alpha so disabled state reads visually.
   * Scene calls this from setRollEnabled.
   */
  setRollReady(ready: boolean): void {
    if (this.rollReady === ready) return;
    this.rollReady = ready;
    if (ready && this.pickUpY === undefined) this.startIdleJiggle();
    if (!ready) this.stopIdleJiggle();
    // Don't dim while the dice are mid-throw; drop() reapplies on landing.
    if (this.pickUpY === undefined) {
      this.animateAlphaTo(ready ? 1.0 : DICE_DIM_ALPHA);
    }
  }

  private animateAlphaTo(target: number): void {
    this.alphaTween?.stop();
    this.alphaTween = this.scene.tweens.add({
      targets: this,
      alpha: target,
      duration: 220,
      ease: 'Sine.easeOut',
    });
  }

  /**
   * Subtle "waiting to be thrown" wobble. Tweaks only the inner sprite's
   * local transform so it doesn't fight DiceThrow's per-frame writes to the
   * container's x/y/angle during grab/throw/settle/return.
   */
  startIdleJiggle(): void {
    if (this.idleJiggleTween) return;
    this.idleJiggleTween = this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 4000,
      repeat: -1,
      onUpdate: () => {
        const t = this.scene.time.now / 1000 + this.idleJigglePhase;
        // High-frequency multi-sine buzz — actual position jitter, not just
        // a sway. Layered freqs make it feel organic instead of robotic.
        // Plus a slow ~2s envelope that pulses bigger "shakes" every couple
        // seconds so the dice read as eager, not just vibrating.
        const envelope = 0.7 + 0.3 * Math.sin(t * 1.3);
        const dx = (Math.sin(t * 14.3) * 2.0 + Math.sin(t * 27.1) * 1.0) * envelope;
        const dy = (Math.cos(t * 12.7) * 1.8 + Math.cos(t * 25.4) * 0.9) * envelope;
        const angDeg = (Math.sin(t * 16.2) * 3.5 + Math.sin(t * 9.4) * 1.5) * envelope;
        this.sprite.setRotation((angDeg * Math.PI) / 180);
        this.sprite.setPosition(dx, dy);
      },
    });
  }

  stopIdleJiggle(): void {
    this.idleJiggleTween?.stop();
    this.idleJiggleTween = undefined;
    this.sprite.setRotation(0);
    this.sprite.setPosition(0, 0);
  }

  startHinting() {
    if (this.halo) return;
    const r = (this.width ?? 48) * 0.85;
    const g = this.scene.add.graphics();
    g.lineStyle(Math.max(2, Math.round(r * 0.06)), 0xd4af37, 1);
    g.strokeCircle(0, 0, r);
    this.add(g);
    this.halo = g;
    this.haloTween = this.scene.tweens.add({
      targets: g,
      alpha: { from: 0.3, to: 1.0 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  stopHinting() {
    this.haloTween?.stop();
    this.haloTween = undefined;
    this.halo?.destroy();
    this.halo = undefined;
  }

  /**
   * Direct transform setter used by DiceThrow each physics frame.
   * Bypasses tweens. Position is in scene coordinates (the Container's parent).
   */
  setPhysicsTransform(x: number, y: number, angleDeg: number): void {
    this.x = x;
    this.y = y;
    this.angle = angleDeg;
  }

  /**
   * Visual lift on grab — small upward offset so the dice "rise" with the
   * finger. No alpha or halo: the player should never see the dice change
   * appearance, only position.
   */
  private pickUpY: number | undefined;
  pickUp(liftPx = 8): void {
    if (this.pickUpY !== undefined) return;
    this.pickUpY = this.y;
    this.y -= liftPx;
    this.stopIdleJiggle();
    // Always full opacity in flight — no fading mid-throw, even if
    // rollReady flips false during settle (e.g., seven-out clears the bet).
    this.alphaTween?.stop();
    this.alphaTween = undefined;
    this.setAlpha(1.0);
  }
  drop(): void {
    if (this.pickUpY === undefined) return;
    this.y = this.pickUpY;
    this.pickUpY = undefined;
    if (this.rollReady) this.startIdleJiggle();
    // Reapply target alpha now that we're back at home — rollReady may have
    // flipped during the throw and the alpha tween was suppressed then.
    this.animateAlphaTo(this.rollReady ? 1.0 : DICE_DIM_ALPHA);
  }

  private flickerEvent: Phaser.Time.TimerEvent | undefined;

  /**
   * Begin random-face flicker at the given cadence. Idempotent — calling
   * again with a different interval restarts the timer.
   */
  startFaceFlicker(intervalMs: number): void {
    this.flickerEvent?.remove();
    this.flickerEvent = this.scene.time.addEvent({
      delay: intervalMs,
      loop: true,
      callback: () => this.showFace(1 + Math.floor(Math.random() * 6)),
    });
  }

  stopFaceFlicker(): void {
    this.flickerEvent?.remove();
    this.flickerEvent = undefined;
  }

  showFace(face: number) {
    this.sprite.setTexture(getDieTexture(this.scene, face));
  }

  /** Animate a tumble that resolves to `finalFace`. ms = total tumble duration. */
  tumbleTo(finalFace: number, ms = 800): Promise<void> {
    return new Promise((resolve) => {
      const startTime = this.scene.time.now;
      const evt = this.scene.time.addEvent({
        delay: 50,
        loop: true,
        callback: () => {
          if (this.scene.time.now - startTime >= ms) {
            evt.remove();
            this.showFace(finalFace);
            resolve();
          } else {
            this.showFace(1 + Math.floor(Math.random() * 6));
          }
        },
      });
      this.scene.tweens.add({
        targets: this,
        angle: { from: 0, to: 360 + Math.random() * 720 },
        duration: ms,
        ease: 'Cubic.Out',
      });
    });
  }
}
